import React, { useState, useEffect } from "react";
import { getImageUrlAdminToken } from "../../utills/ImagesUtilsAdmin";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Card,
  Button,
  Avatar,
  CardHeader,
  Typography,
  Pagination,
  Stack,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SearchAppBar from "../SearchAppBar";
import Slider from "../Slider";
import ReadMoretext from "../../../utils/ReadMoretext";
import { getPosts, actionOnPost } from "../../services/post_Service";
import PostSkeleton from "../../skelton/PostSkelton";
import CustomSnackbar from "../../CustomSnackbar";
import AdminWorkAsPost from "./AdminWorkAsPost";
import { generateAdminURL } from "../../utills/HelperUrl";
import ProjectCard from "../projects/ProjectCard";

const All_post = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const ismobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [status, setStatus] = useState(3);
  const [posts, setPosts] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

 // console.log("All_post", posts);

  const displayPosts = searchResults ? searchResults.data : posts;

  const handleSearchResults = (results) => {
    setSearchResults(results);
    if (results && results.pagination) {
      setTotalPages(results.pagination.last_page);
      setPage(results.pagination.current_page);
    }
  };

  const handleChange = (value) => {
    setLoading(true);
    setStatus(value);
    setPage(1);
    setSearchResults(null);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const fetchData = async () => {
    try {
      const data = await getPosts(status, page);
      setPosts(data.data);
      setTotalPages(data.pagination.last_page);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch posts", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchResults) {
      fetchData();
    }
  }, [status, page]);

  useEffect(() => {
    setSearchResults(null);
    setPage(1);
  }, [status]);

  const handleAction = async (postId, status) => {
    try {
      const data = await actionOnPost(postId, status);
      setSnackbarMessage(data.message || "Post action successful.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      // Update the post status in local state immediately
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId ? { ...post, status: status } : post
        )
      );

      // Also update search results if they exist
      if (searchResults) {
        setSearchResults(prevResults => ({
          ...prevResults,
          data: prevResults.data.map(post =>
            post.id === postId ? { ...post, status: status } : post
          )
        }));
      }
    } catch (error) {
      setSnackbarMessage(
        error.data.message || "Failed to perform action on post."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const ActionButtons = ({ post }) => (

    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexDirection: ismobile ? "column" : "row",
        width: ismobile ? "100%" : "auto",
        mt: ismobile ? 1 : 0,
      }}
    >
      {post.status === 0 &&
        post.work_content.length === 0 &&
        post.project_content.length === 0 && (
          <>
            <Button
              disabledElevation
              variant="outlined"
              color="success"
              onClick={() => handleAction(post.id, 2)}
              fullWidth={ismobile}
              size={ismobile ? "small" : "medium"}
              sx={{ textTransform: "none" }}
            >
              Approve
            </Button>
            <Button
              disabledElevation
              variant="outlined"
              color="error"
              onClick={() => handleAction(post.id, 1)}
              fullWidth={ismobile}
              size={ismobile ? "small" : "medium"}
              sx={{ textTransform: "none" }}
            >
              Block
            </Button>
          </>
        )}

      {post.status === 1 &&
        post.work_content.length === 0
        && post.project_content.length === 0 && (
          <Button
            disabledElevation
            variant="outlined"
            color="warning"
            onClick={() => handleAction(post.id, 0)}
            fullWidth={ismobile}
            size={ismobile ? "small" : "medium"}
            sx={{ textTransform: "none" }}
          >
            Unblock
          </Button>
        )}
      {post.status === 2 &&
        post.work_content.length === 0 &&
        post.project_content.length === 0 && (
          <Button
            disabledElevation
            variant="outlined"
            color="error"
            onClick={() => handleAction(post.id, 1)}
            fullWidth={ismobile}
            size={ismobile ? "small" : "medium"}
            sx={{ textTransform: "none" }}
          >
            Block
          </Button>
        )}

      {post.work_content.length > 0 && (
        <Button
          variant="outlined"
          color="primary"
          disabledElevation
          sx={{ textTransform: "none" }}
          onClick={() => navigate(generateAdminURL("jobs", post.work_content[0].work_listing_id, post.work_content[0].title))}
          fullWidth={ismobile}
          size={ismobile ? "small" : "medium"}
        >
          View Detail
        </Button>
      )}
    </Box>
  );


  const renderPostContent = (post) => {
    // Check for work_content first
    if (post.work_content && post.work_content.length > 0) {
      return (
        <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
          <AdminWorkAsPost workContent={post.work_content[0]} />
        </Grid>
      );
    }

    // Check for project_content
    if (post.project_content && post.project_content.length > 0) {
      return (
        <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
          <ProjectCard projectContent={post.project_content[0]} type="Allpost" />
        </Grid>
      );
    }

    // Default to regular post content (text and media)
    return (
      <>
        {post.text_content && (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              width: "100%",
              maxWidth: ismobile ? "100%" : "400px",
            }}
          >
            <ReadMoretext
              text={post.text_content}
              maxLength={ismobile ? 300 : 600}
            />
          </Grid>
        )}
        {post.post_content?.length > 0 && (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              width: ismobile ? "100%" : "auto",
              maxWidth: ismobile ? "100%" : "500px",
              mt: ismobile ? 1 : 0,
            }}
          >
            <Slider
              mediaContent={post.post_content.map((content) =>
                getImageUrlAdminToken(content.media_content_blob)
              )}
            />
          </Grid>
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      <Container
        maxWidth="lg"
        sx={{ py: ismobile ? 1 : 3, px: ismobile ? 1 : 3 }}
      >
        <Paper
          elevation={1}
          sx={{
            position: "sticky",
            top: ismobile ? 56 : 60,
            mb: ismobile ? 1 : 2,
            zIndex: 10,
          }}
        >
          <SearchAppBar
            status={status}
            handleChange={handleChange}
            SearchData={handleSearchResults}
            fetchData={fetchData}
            currentPage={page}
          />
        </Paper>

        {displayPosts.length === 0 && !loading && (
          <Box
            sx={{
              textAlign: "center",
              mt: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CameraAltIcon
              sx={{ fontSize: ismobile ? 60 : 100, color: "action" }}
            />
            <Typography
              variant={ismobile ? "h5" : "h4"}
              color="textSecondary"
              align="center"
            >
              No posts found.
            </Typography>
          </Box>
        )}

        {loading ? (
          <PostSkeleton count={ismobile ? 3 : 5} />
        ) : (
          displayPosts.map((post) => (
            <Paper
              elevation={0}
              key={post.id}
              sx={{
                mb: ismobile ? 2 : 1,
                overflow: "hidden",
              }}
            >
              <Card elevation={0}>
                <CardHeader
                  sx={{
                    p: ismobile ? 1.5 : 2,
                    "& .MuiCardHeader-content": {
                      overflow: "hidden",
                    },
                  }}
                  avatar={
                    <Avatar
                      src={
                        post.user_thumbnail_pic
                          ? getImageUrlAdminToken(post.user_thumbnail_pic)
                          : "https://source.unsplash.com/random"
                      }
                      alt={post.name}
                      sx={{
                        width: ismobile ? 32 : 40,
                        height: ismobile ? 32 : 40,
                        cursor: "pointer",
                      }}
                      onClick={() => navigate(`/users/${post.user_id}`)}

                    >
                      {post.name ? post.name.charAt(0).toUpperCase() : ""}
                    </Avatar>
                  }
                  title={
                    <Typography
                      variant={ismobile ? "body1" : "subtitle1"}
                      sx={{
                        fontWeight: "medium",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {post.name}
                    </Typography>
                  }
                  subheader={
                    <Typography
                      variant={ismobile ? "caption" : "body2"}
                      color="text.secondary"
                    >
                      {new Date(post.created_at).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",  // 'long' gives full month name like "May"
                        year: "numeric",
                      })}
                    </Typography>
                  }


                  action={!ismobile && <ActionButtons post={post} />}
                />

                <Grid
                  container
                  spacing={ismobile ? 1 : 2}
                  sx={{
                    px: ismobile ? 1.5 : 2,
                    pb: ismobile ? 1.5 : 1,
                    flexDirection: ismobile ? "column" : "row",
                  }}
                >
                  {renderPostContent(post)}
                </Grid>


                {ismobile && (
                  <Box sx={{ p: 1.5, pt: 0 }}>
                    <ActionButtons post={post} />
                  </Box>
                )}
              </Card>
            </Paper>
          ))
        )}

        <Stack
          spacing={2}
          sx={{ display: "flex", alignItems: "center", mt: 2 }}
        >
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              color="primary"
              size={ismobile ? "small" : "medium"}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": {
                  minWidth: ismobile ? 32 : 40,
                  height: ismobile ? 32 : 40,
                },
              }}
            />
          )}
        </Stack>
      </Container>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </React.Fragment>
  );
};

export default All_post;
